/* PipelineManagement — /admin/pipelines
   - Pipeline list with active/inactive badges
   - Stage list with reorder arrows (styled like defendant expand), active/inactive toggle
   - Pipeline modal: name, display_name, description, is_active (no doc_base_directory)
   - Stage modal: stage_name, description, is_active
   - Reorder sends { stages: [{ stage_id, stage_order }] } */
import { useState } from 'react';
import css from '../AdminPage.module.css';
import {
  usePipelines,
  usePipelineStages,
  useCreatePipeline,
  useUpdatePipeline,
  useDeletePipeline,
  useCreateStage,
  useUpdateStage,
  useDeleteStage,
  useReorderStages,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import StageGuardModal from './StageGuardModal';
import type { Pipeline, PipelineStage } from '../../../types/admin';

export default function PipelineManagement() {
  const { data: pipelines = [], isLoading } = usePipelines();
  const { showToast } = useToast();

  /* Guards modal state */
  const [guardsModalStage, setGuardsModalStage] = useState<{
    id: number;
    name: string;
    caseType: string;
  } | null>(null);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { data: stages = [] } = usePipelineStages(selectedId);

  const createPipeline = useCreatePipeline();
  const updatePipeline = useUpdatePipeline();
  const deletePipeline = useDeletePipeline();
  const createStage = useCreateStage();
  const updateStage = useUpdateStage();
  const deleteStage = useDeleteStage();
  const reorderStages = useReorderStages();

  /* modal state */
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState<Partial<Pipeline> | null>(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [editingStage, setEditingStage] = useState<Partial<
    PipelineStage & { pipeline_id: number }
  > | null>(null);

  /* pipeline form */
  const [pName, setPName] = useState('');
  const [pDisplay, setPDisplay] = useState('');
  const [pDesc, setPDesc] = useState('');
  const [pActive, setPActive] = useState(true);

  /* stage form */
  const [sName, setSName] = useState('');
  const [sDesc, setSDesc] = useState('');
  const [sActive, setSActive] = useState(true);

  function openNewPipeline() {
    setEditingPipeline(null);
    setPName('');
    setPDisplay('');
    setPDesc('');
    setPActive(true);
    setShowPipelineModal(true);
  }

  function openEditPipeline(p: Pipeline) {
    setEditingPipeline(p);
    setPName(p.name);
    setPDisplay(p.display_name);
    setPDesc(p.description || '');
    setPActive(p.is_active !== false);
    setShowPipelineModal(true);
  }

  async function savePipeline() {
    const payload = { name: pName, display_name: pDisplay, description: pDesc, is_active: pActive };
    try {
      if (editingPipeline?.id) {
        await updatePipeline.mutateAsync({ id: editingPipeline.id, ...payload });
        showToast('Pipeline updated', 'success');
      } else {
        await createPipeline.mutateAsync(payload);
        showToast('Pipeline created', 'success');
      }
      setShowPipelineModal(false);
    } catch {
      showToast('Failed to save pipeline', 'error');
    }
  }

  async function removePipeline(id: number) {
    if (!confirm('Delete this pipeline? All associated stages will be removed.')) return;
    try {
      await deletePipeline.mutateAsync(id);
      if (selectedId === id) setSelectedId(null);
      showToast('Pipeline deleted', 'success');
    } catch {
      showToast('Failed to delete pipeline', 'error');
    }
  }

  function openNewStage() {
    if (!selectedId) return;
    setEditingStage(null);
    setSName('');
    setSDesc('');
    setSActive(true);
    setShowStageModal(true);
  }

  function openEditStage(s: PipelineStage) {
    if (!selectedId) return;
    setEditingStage({ ...s, pipeline_id: selectedId });
    setSName(s.stage_name);
    setSDesc(s.description || '');
    setSActive(s.is_active !== false);
    setShowStageModal(true);
  }

  async function saveStage() {
    if (!selectedId) return;
    const payload = { stage_name: sName, description: sDesc, is_active: sActive };
    try {
      if (editingStage?.stage_id) {
        await updateStage.mutateAsync({
          pipeline_id: selectedId,
          stage_id: editingStage.stage_id,
          ...payload,
        });
        showToast('Stage updated', 'success');
      } else {
        await createStage.mutateAsync({
          pipeline_id: selectedId,
          ...payload,
          sort_order: stages.length,
          case_type_id: selectedId,
        });
        showToast('Stage created', 'success');
      }
      setShowStageModal(false);
    } catch {
      showToast('Failed to save stage', 'error');
    }
  }

  async function removeStage(stageId: number) {
    if (!selectedId || !confirm('Delete this stage?')) return;
    try {
      await deleteStage.mutateAsync({ pipeline_id: selectedId, stage_id: stageId });
      showToast('Stage deleted', 'success');
    } catch {
      showToast('Failed to delete stage', 'error');
    }
  }

  async function moveStage(idx: number, dir: -1 | 1) {
    if (!selectedId) return;
    const sorted = [...stages].sort((a, b) => a.sort_order - b.sort_order);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= sorted.length) return;
    [sorted[idx], sorted[newIdx]] = [sorted[newIdx], sorted[idx]];
    // Build { stages: [{ stage_id, stage_order }] }
    const payload = sorted.map((s, i) => ({ stage_id: s.stage_id, stage_order: i + 1 }));
    try {
      await reorderStages.mutateAsync({ pipeline_id: selectedId, stages: payload });
    } catch {
      showToast('Failed to reorder', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  const sortedStages = [...stages].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <>
      <div className={css.section}>
        <div className={css.sectionHeader}>
          <div>
            <h2 className={css.sectionTitle}>🔀 Pipeline Management</h2>
            <p className={css.sectionSubtitle}>Manage case type pipelines and their stages</p>
          </div>
          <button className={css.btnPrimary} onClick={openNewPipeline}>
            + New Pipeline
          </button>
        </div>

        <div className={css.grid2}>
          {/* Pipeline list */}
          <div>
            <h3
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--gray-600)',
                marginBottom: 8,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Pipelines
            </h3>
            {pipelines.length === 0 ? (
              <div className={css.emptyState}>
                <p className={css.emptyMessage}>No pipelines yet</p>
              </div>
            ) : (
              pipelines.map((p) => (
                <div
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: 'var(--radius-md)',
                    border:
                      selectedId === p.id
                        ? '2px solid var(--primary-500)'
                        : '1px solid var(--gray-200)',
                    background:
                      selectedId === p.id ? 'var(--primary-50)' : 'var(--surface-primary)',
                    marginBottom: 8,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    opacity: p.is_active === false ? 0.6 : 1,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 'var(--font-size-sm)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      {p.display_name || p.name}
                      <span
                        className={p.is_active === false ? css.badgeWarning : css.badgeSuccess}
                        style={{ fontSize: 'var(--font-size-xs)' }}
                      >
                        {p.is_active === false ? 'Inactive' : 'Active'}
                      </span>
                    </div>
                    {p.description && (
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                        {p.description}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      className={`${css.btnSecondary} ${css.btnSmall}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditPipeline(p);
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      className={`${css.btnDanger} ${css.btnSmall}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        removePipeline(p.id);
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Stages */}
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <h3
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--gray-600)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Stages {selectedId ? `(${sortedStages.length})` : ''}
              </h3>
              {selectedId && (
                <button className={`${css.btnPrimary} ${css.btnSmall}`} onClick={openNewStage}>
                  + Stage
                </button>
              )}
            </div>
            {!selectedId ? (
              <div className={css.emptyState}>
                <p className={css.emptyMessage}>Select a pipeline to view stages</p>
              </div>
            ) : sortedStages.length === 0 ? (
              <div className={css.emptyState}>
                <p className={css.emptyMessage}>No stages yet</p>
              </div>
            ) : (
              sortedStages.map((s, i) => (
                <div
                  key={s.stage_id}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--gray-200)',
                    borderLeft:
                      s.is_active === false
                        ? '3px solid var(--danger-500, #ef4444)'
                        : '1px solid var(--gray-200)',
                    background:
                      s.is_active === false ? 'var(--error-50)' : 'var(--surface-primary)',
                    marginBottom: 6,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    opacity: s.is_active === false ? 0.75 : 1,
                  }}
                >
                  {/* Reorder arrows — styled like defendant expand */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <button
                      onClick={() => moveStage(i, -1)}
                      disabled={i === 0}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: i === 0 ? 'default' : 'pointer',
                        padding: '2px 4px',
                        lineHeight: 1,
                        fontSize: '0.65rem',
                        opacity: i === 0 ? 0.25 : 0.7,
                        transition: 'opacity 0.15s, transform 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        if (i !== 0) e.currentTarget.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        if (i !== 0) e.currentTarget.style.opacity = '0.7';
                      }}
                      title="Move up"
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          transform: 'rotate(-90deg)',
                          transition: 'transform 0.15s',
                        }}
                      >
                        ▶
                      </span>
                    </button>
                    <button
                      onClick={() => moveStage(i, 1)}
                      disabled={i === sortedStages.length - 1}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: i === sortedStages.length - 1 ? 'default' : 'pointer',
                        padding: '2px 4px',
                        lineHeight: 1,
                        fontSize: '0.65rem',
                        opacity: i === sortedStages.length - 1 ? 0.25 : 0.7,
                        transition: 'opacity 0.15s, transform 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        if (i !== sortedStages.length - 1) e.currentTarget.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        if (i !== sortedStages.length - 1) e.currentTarget.style.opacity = '0.7';
                      }}
                      title="Move down"
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          transform: 'rotate(90deg)',
                          transition: 'transform 0.15s',
                        }}
                      >
                        ▶
                      </span>
                    </button>
                  </div>
                  <span className={css.badgeNeutral} style={{ minWidth: 28, textAlign: 'center' }}>
                    {i + 1}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 'var(--font-size-sm)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      {s.stage_name}
                      {s.is_active === false && (
                        <span
                          style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--danger-600, #dc2626)',
                            fontWeight: 400,
                            fontStyle: 'italic',
                          }}
                        >
                          (archived)
                        </span>
                      )}
                    </div>
                    {s.description && (
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                        {s.description}
                      </div>
                    )}
                  </div>
                  <span
                    className={s.is_active === false ? css.badgeWarning : css.badgeSuccess}
                    style={{ fontSize: 'var(--font-size-xs)' }}
                  >
                    {s.is_active === false ? 'Archived' : 'Active'}
                  </span>
                  <button
                    className={`${css.btnSecondary} ${css.btnSmall}`}
                    onClick={() => openEditStage(s)}
                  >
                    ✏️
                  </button>
                  <button
                    className={`${css.btnSecondary} ${css.btnSmall}`}
                    title="Stage Guards"
                    onClick={() =>
                      setGuardsModalStage({
                        id: s.stage_id,
                        name: s.stage_name,
                        caseType: pipelines.find((p) => p.id === selectedId)?.display_name ?? '',
                      })
                    }
                  >
                    🛡️
                  </button>
                  <button
                    className={`${css.btnDanger} ${css.btnSmall}`}
                    onClick={() => removeStage(s.stage_id)}
                  >
                    🗑️
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Pipeline modal */}
      {showPipelineModal && (
        <div className={css.modalBackdrop} onClick={() => setShowPipelineModal(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>
                {editingPipeline ? 'Edit Pipeline' : 'New Pipeline'}
              </h3>
              <button className={css.modalClose} onClick={() => setShowPipelineModal(false)}>
                ×
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Internal Name</label>
                <input
                  className={css.formInput}
                  value={pName}
                  onChange={(e) => setPName(e.target.value)}
                  placeholder="e.g. housing_disrepair"
                  readOnly={!!editingPipeline}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Display Name</label>
                <input
                  className={css.formInput}
                  value={pDisplay}
                  onChange={(e) => setPDisplay(e.target.value)}
                  placeholder="e.g. Housing Disrepair"
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Description</label>
                <textarea
                  className={css.formTextarea}
                  value={pDesc}
                  onChange={(e) => setPDesc(e.target.value)}
                />
              </div>
              <div className={css.formGroup}>
                <label
                  className={css.formLabel}
                  style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={pActive}
                    onChange={(e) => setPActive(e.target.checked)}
                  />
                  Active
                </label>
                <p
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--gray-500)',
                    marginTop: 4,
                  }}
                >
                  Inactive pipelines are hidden from users but preserved in the system
                </p>
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowPipelineModal(false)}>
                Cancel
              </button>
              <button
                className={css.btnPrimary}
                onClick={savePipeline}
                disabled={!pName || createPipeline.isPending || updatePipeline.isPending}
              >
                {editingPipeline ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stage modal */}
      {showStageModal && (
        <div className={css.modalBackdrop} onClick={() => setShowStageModal(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>{editingStage ? 'Edit Stage' : 'New Stage'}</h3>
              <button className={css.modalClose} onClick={() => setShowStageModal(false)}>
                ×
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Stage Name</label>
                <input
                  className={css.formInput}
                  value={sName}
                  onChange={(e) => setSName(e.target.value)}
                  placeholder="e.g. Pre-Action"
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Description</label>
                <textarea
                  className={css.formTextarea}
                  value={sDesc}
                  onChange={(e) => setSDesc(e.target.value)}
                />
              </div>
              <div className={css.formGroup}>
                <label
                  className={css.formLabel}
                  style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={sActive}
                    onChange={(e) => setSActive(e.target.checked)}
                  />
                  Active
                </label>
                <p
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--gray-500)',
                    marginTop: 4,
                  }}
                >
                  Archived stages are hidden from case views but preserved in the system
                </p>
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowStageModal(false)}>
                Cancel
              </button>
              <button
                className={css.btnPrimary}
                onClick={saveStage}
                disabled={!sName || createStage.isPending || updateStage.isPending}
              >
                {editingStage ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stage Guards modal — scoped to a specific stage */}
      {guardsModalStage && (
        <StageGuardModal
          stageId={guardsModalStage.id}
          stageName={guardsModalStage.name}
          caseTypeName={guardsModalStage.caseType}
          onClose={() => setGuardsModalStage(null)}
        />
      )}
    </>
  );
}
